// ============================================================
// CLUBHUB – Gruppen: Regeln, die Masken und Backend teilen
// ============================================================
// Baum, Zaehlen, Zustand einer Zugehoerigkeit, Funktionen. Rein und
// browsertauglich: der Gruppenbaum rechnet im Browser, die Pruefungen
// beim Speichern (`lib/gruppen/speicher.ts`) dieselben Funktionen auf
// dem Server.
//
// FUNKTION IST KEIN RECHT. Wie in ClubDesk ist eine Funktion ein Amt
// (1. Vorsitzende, Kassenwart, Trainerin) und steht an der
// Zugehoerigkeit. Was jemand im System DARF, regelt allein die Rolle
// (`lib/permissions.ts`, Einstellungen › Benutzer). Die Masken sagen
// das an jeder Stelle, an der eine Funktion vergeben wird.
// ============================================================
import type { Gender, GroupKind } from "@/generated/prisma/enums";
import type { ContactView } from "@/lib/views";
import type { GruppeView, PersonKurzView, ZugehoerigkeitView } from "./views";
import { plusTage, tageZwischen } from "./datum";

// ── Zustand einer Zugehoerigkeit ────────────────────────────

export type ZugehoerigkeitsStand = "aktuell" | "kuenftig" | "beendet";

/** Aktuell ist, wo `since` erreicht ist und `until` leer oder nicht vorbei. */
export function stand(z: Pick<ZugehoerigkeitView, "since" | "until">, heute: string): ZugehoerigkeitsStand {
  if (z.since && z.since > heute) return "kuenftig";
  if (z.until && z.until < heute) return "beendet";
  return "aktuell";
}

/** Endet in den naechsten 90 Tagen (geplantes Ende, Saisonwechsel). */
export function endetBald(z: Pick<ZugehoerigkeitView, "until">, heute: string): boolean {
  return Boolean(z.until && z.until >= heute && tageZwischen(heute, z.until) <= 90);
}

/** War die Person am Tag `tag` in dieser Gruppe — in irgendeinem Zeitraum ausser `ohne`? */
export function warDabei(eigene: ZugehoerigkeitView[], contactId: string, tag: string, ohne?: string): boolean {
  return eigene.some(
    (z) => z.id !== ohne && z.contactId === contactId && (!z.since || z.since <= tag) && (!z.until || z.until >= tag),
  );
}

/**
 * Zu- und Abgaenge einer Gruppe: Personen, deren Zeitraum in
 * (`von`, `bis`] beginnt bzw. in [`von`, `bis`) endet. Ein Wechsel der
 * Funktion zum Stichtag (alter Zeitraum endet am Vortag, neuer
 * beginnt) ist weder Zugang noch Abgang — die Person war ja da.
 */
export function bewegungen(
  eigene: ZugehoerigkeitView[],
  von: string,
  bis: string,
): { zugaenge: number; abgaenge: number } {
  const zu = new Set<string>();
  const ab = new Set<string>();
  for (const z of eigene) {
    if (z.since && z.since > von && z.since <= bis && !warDabei(eigene, z.contactId, plusTage(z.since, -1), z.id)) zu.add(z.contactId);
    if (z.until && z.until >= von && z.until < bis && !warDabei(eigene, z.contactId, plusTage(z.until, 1), z.id)) ab.add(z.contactId);
  }
  return { zugaenge: zu.size, abgaenge: ab.size };
}

/**
 * Was unter „Aktuell“ steht: die laufenden Zeitraeume, dazu kuenftige
 * Zugaenge — aber nur von Personen, die heute nicht schon dabei sind
 * (ein Funktionswechsel zu einem spaeteren Stichtag steht als Hinweis
 * an der laufenden Zeile, nicht als zweite Zeile).
 */
export function aktuelleZeitraeume(eigene: ZugehoerigkeitView[], heute: string): ZugehoerigkeitView[] {
  const laufend = eigene.filter((z) => stand(z, heute) === "aktuell");
  const dabei = new Set(laufend.map((z) => z.contactId));
  const kuenftig = new Map<string, ZugehoerigkeitView>();
  for (const z of eigene) {
    if (stand(z, heute) !== "kuenftig" || dabei.has(z.contactId)) continue;
    const da = kuenftig.get(z.contactId);
    if (!da || (z.since ?? "") < (da.since ?? "")) kuenftig.set(z.contactId, z);
  }
  return [...laufend, ...kuenftig.values()];
}

// ── Baum ────────────────────────────────────────────────────

export function kinderVon(gruppen: GruppeView[], id: string | null): GruppeView[] {
  return gruppen.filter((g) => g.parentId === id);
}

/** Alle Gruppen unterhalb von `id` (ohne sie selbst). Braucht nur `id` und `parentId`. */
export function nachfahren(gruppen: Pick<GruppeView, "id" | "parentId">[], id: string): string[] {
  const ergebnis: string[] = [];
  const offen = [id];
  while (offen.length) {
    const aktuell = offen.pop()!;
    for (const k of gruppen) {
      if (k.parentId === aktuell && !ergebnis.includes(k.id)) {
        ergebnis.push(k.id);
        offen.push(k.id);
      }
    }
  }
  return ergebnis;
}

/** Der Weg von oben bis zu `id` (ohne sie selbst): [Jugend] fuer U13. */
export function vorfahren(gruppen: GruppeView[], id: string): GruppeView[] {
  const weg: GruppeView[] = [];
  let g = gruppen.find((x) => x.id === id);
  const gesehen = new Set<string>();
  while (g?.parentId && !gesehen.has(g.parentId)) {
    gesehen.add(g.parentId);
    const eltern = gruppen.find((x) => x.id === g!.parentId);
    if (!eltern) break;
    weg.unshift(eltern);
    g = eltern;
  }
  return weg;
}

/** „Jugend › U13“ */
export function pfadText(gruppen: GruppeView[], id: string): string {
  const g = gruppen.find((x) => x.id === id);
  return [...vorfahren(gruppen, id).map((v) => v.name), g?.name].filter(Boolean).join(" › ");
}

export type BaumZeile = {
  gruppe: GruppeView;
  tiefe: number;
  hatKinder: boolean;
  /** Steht nur als Weg zu einem Treffer da, passt selbst nicht. */
  nurPfad: boolean;
};

/**
 * Den Baum flach legen — Eltern vor Kindern, Geschwister in der
 * Reihenfolge von `ordnen`. `treffer` sind die Gruppen, die zum Filter
 * passen; ihre Vorfahren kommen als Weg dazu (`nurPfad`), damit eine
 * gefundene U13 nicht ohne ihre Jugend dasteht.
 */
export function baumZeilen(
  gruppen: GruppeView[],
  treffer: Set<string>,
  ordnen: (geschwister: GruppeView[]) => GruppeView[],
): BaumZeile[] {
  const sichtbar = new Set<string>();
  for (const id of treffer) {
    sichtbar.add(id);
    for (const v of vorfahren(gruppen, id)) sichtbar.add(v.id);
  }
  const zeilen: BaumZeile[] = [];
  const ids = new Set(gruppen.map((g) => g.id));
  const wurzeln = gruppen.filter((g) => !g.parentId || !ids.has(g.parentId));

  function legen(liste: GruppeView[], tiefe: number) {
    for (const g of ordnen(liste.filter((x) => sichtbar.has(x.id)))) {
      const kinder = gruppen.filter((k) => k.parentId === g.id);
      zeilen.push({
        gruppe: g,
        tiefe,
        hatKinder: kinder.some((k) => sichtbar.has(k.id)),
        nurPfad: !treffer.has(g.id),
      });
      legen(kinder, tiefe + 1);
    }
  }
  legen(wurzeln, 0);
  return zeilen;
}

// ── Zaehlen ─────────────────────────────────────────────────

/** Die Personen, die heute in `gruppeId` sind (je Person einmal). */
export function aktuellePersonen(
  zugehoerigkeiten: ZugehoerigkeitView[],
  gruppeIds: string[],
  heute: string,
): Set<string> {
  const ids = new Set(gruppeIds);
  const personen = new Set<string>();
  for (const z of zugehoerigkeiten) {
    if (ids.has(z.groupId) && stand(z, heute) === "aktuell") personen.add(z.contactId);
  }
  return personen;
}

export type Mitgliederzahl = {
  /** Direkt in der Gruppe. */
  direkt: number;
  /** Mit allen Untergruppen, jede Person einmal. */
  gesamt: number;
};

export function mitgliederzahl(
  gruppen: GruppeView[],
  zugehoerigkeiten: ZugehoerigkeitView[],
  id: string,
  heute: string,
): Mitgliederzahl {
  return {
    direkt: aktuellePersonen(zugehoerigkeiten, [id], heute).size,
    gesamt: aktuellePersonen(zugehoerigkeiten, [id, ...nachfahren(gruppen, id)], heute).size,
  };
}

// ── Funktionen (Aemter) ─────────────────────────────────────

type Vorschlag = { w: string; m: string; n: string };

/** Die gaengigen Aemter je Gruppenart — in der Form, die zur Person passt. */
const AEMTER: Record<GroupKind, Vorschlag[]> = {
  BOARD: [
    { w: "1. Vorsitzende", m: "1. Vorsitzender", n: "1. Vorsitz" },
    { w: "2. Vorsitzende", m: "2. Vorsitzender", n: "2. Vorsitz" },
    { w: "Schatzmeisterin", m: "Schatzmeister", n: "Schatzmeisterei" },
    { w: "Kassenwartin", m: "Kassenwart", n: "Kasse" },
    { w: "Schriftführerin", m: "Schriftführer", n: "Schriftführung" },
    { w: "Jugendleiterin", m: "Jugendleiter", n: "Jugendleitung" },
    { w: "Beisitzerin", m: "Beisitzer", n: "Beisitz" },
    { w: "Kassenprüferin", m: "Kassenprüfer", n: "Kassenprüfung" },
  ],
  DEPARTMENT: [
    { w: "Abteilungsleiterin", m: "Abteilungsleiter", n: "Abteilungsleitung" },
    { w: "Stellv. Abteilungsleiterin", m: "Stellv. Abteilungsleiter", n: "Stellv. Abteilungsleitung" },
    { w: "Jugendleiterin", m: "Jugendleiter", n: "Jugendleitung" },
    { w: "Kassenwartin", m: "Kassenwart", n: "Kasse" },
  ],
  TEAM: [
    { w: "Trainerin", m: "Trainer", n: "Training" },
    { w: "Co-Trainerin", m: "Co-Trainer", n: "Co-Training" },
    { w: "Torwarttrainerin", m: "Torwarttrainer", n: "Torwarttraining" },
    { w: "Betreuerin", m: "Betreuer", n: "Betreuung" },
    { w: "Kapitänin", m: "Kapitän", n: "Spielführung" },
  ],
  COMMITTEE: [
    { w: "Leiterin", m: "Leiter", n: "Leitung" },
    { w: "Stellv. Leiterin", m: "Stellv. Leiter", n: "Stellv. Leitung" },
    { w: "Protokollführerin", m: "Protokollführer", n: "Protokoll" },
  ],
  SPONSOR: [
    { w: "Hauptsponsor", m: "Hauptsponsor", n: "Hauptsponsor" },
    { w: "Trikotsponsor", m: "Trikotsponsor", n: "Trikotsponsor" },
    { w: "Bandenwerbung", m: "Bandenwerbung", n: "Bandenwerbung" },
  ],
  FAN: [
    { w: "Sprecherin", m: "Sprecher", n: "Sprecher" },
    { w: "Kassenwartin", m: "Kassenwart", n: "Kasse" },
  ],
  OTHER: [{ w: "Leiterin", m: "Leiter", n: "Leitung" }],
};

/** Vorschlaege fuer das Feld „Funktion“ — passend zur Art und zur Person. */
export function funktionsVorschlaege(kind: GroupKind, gender?: Gender | null): string[] {
  const form = gender === "FEMALE" ? "w" : gender === "MALE" ? "m" : "n";
  return [...new Set(AEMTER[kind].map((v) => v[form]))];
}

/** Rangfolge fuer „mit Funktion zuerst“: Vorsitz vor Kasse vor Beisitz. */
const RANG = [
  /^1\./,
  /^2\./,
  /schatz|kasse(?!npr)/i,
  /schrift|protokoll/i,
  /jugendleit/i,
  /abteilungsleit|^leit|^stellv/i,
  /^trainer|^training/i,
  /co-train|torwart/i,
  /betreu/i,
  /kapit|spielf/i,
  /sprecher/i,
  /haupt/i,
  /beisitz/i,
  /kassenpr/i,
];

export function funktionsRang(funktion: string | null): number {
  if (!funktion) return 999;
  const i = RANG.findIndex((r) => r.test(funktion));
  return i < 0 ? RANG.length : i;
}

// ── Pruefen ─────────────────────────────────────────────────

/** Der Teil vor dem @ — klein, Ziffern, Punkt, Bindestrich. */
export const ALIAS_MUSTER = /^[a-z0-9](?:[a-z0-9.-]{0,38}[a-z0-9])?$/;

/** Fehlersatz zum Alias oder `null`. */
export function aliasFehler(
  alias: string,
  maildomain: string,
  gruppen: GruppeView[],
  eigeneId?: string,
): string | null {
  if (!alias) return null;
  if (!ALIAS_MUSTER.test(alias)) return "Nur Kleinbuchstaben, Ziffern, Punkt und Bindestrich — ohne Leerzeichen und Umlaute.";
  const voll = `${alias}@${maildomain}`;
  const belegt = gruppen.find((g) => g.id !== eigeneId && g.emailAlias === voll);
  if (belegt) return `Die Adresse gehört schon zur Gruppe „${belegt.name}“.`;
  return null;
}

/** Aus einem Gruppennamen einen Alias vorschlagen: „1. Mannschaft“ → „1-mannschaft“. */
export function aliasVorschlag(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/** Alter in vollen Jahren am Stichtag. */
export function alterAm(geburt: string | null, heute: string): number | null {
  if (!geburt) return null;
  const [gy, gm, gd] = geburt.split("-").map(Number);
  const [hy, hm, hd] = heute.split("-").map(Number);
  let a = hy! - gy!;
  if (hm! < gm! || (hm === gm && hd! < gd!)) a--;
  return a;
}

// ── Personen ────────────────────────────────────────────────

/** Was die Gruppen und die Pinnwand von einer Person brauchen — aus dem Personenbestand. */
export function personKurz(c: ContactView): PersonKurzView {
  return {
    id: c.id,
    displayName: c.displayName,
    firstName: c.firstName ?? c.organizationName ?? c.displayName,
    lastName: c.lastName ?? "",
    memberNumber: c.memberNumber,
    status: c.status,
    gender: c.gender,
    birthDate: c.birthDate,
    email: c.email,
    city: c.city,
    isOrganization: c.kind === "ORGANIZATION",
    consentPhotos: c.consentPhotos,
    consentInternet: c.consentInternet,
  };
}

/**
 * Die Personenauswahl (`KontaktAuswahl`) erwartet `ContactView` —
 * sie liest davon nur Name, Nummer, Ort, Mail und Status. So bekommt
 * der Browser nur die Kurzform, nicht Anschrift, Telefon und Notizen.
 */
export function alsAuswahlKontakt(p: PersonKurzView): ContactView {
  return {
    id: p.id,
    kind: p.isOrganization ? "ORGANIZATION" : "PERSON",
    memberNumber: p.memberNumber,
    salutation: null,
    title: null,
    firstName: p.isOrganization ? null : p.firstName,
    lastName: p.isOrganization ? null : p.lastName,
    organizationName: p.isOrganization ? p.displayName : null,
    displayName: p.displayName,
    birthDate: null,
    gender: p.gender,
    street: null,
    zip: null,
    city: p.city,
    country: "DE",
    email: p.email,
    phone: null,
    mobile: null,
    status: p.status,
    entryDate: null,
    exitDate: null,
    householdId: null,
    communication: "EMAIL",
    consentNewsletter: false,
    consentPhotos: p.consentPhotos,
    consentInternet: p.consentInternet,
    groups: [],
    feeTypeId: null,
    paymentMethod: "TRANSFER",
    hasMandate: false,
    openAmount: 0,
    notes: null,
    customFields: {},
    createdAt: "",
  };
}

/** Name fuer den Kreis einer Person. */
export function kreisName(p: PersonKurzView | undefined): { firstName: string; lastName: string } {
  if (!p) return { firstName: "?", lastName: "" };
  return { firstName: p.firstName, lastName: p.lastName };
}
